import React, { useEffect, useMemo, useState } from 'react';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageAdminSpecialTasks from './useManageAdminSpecialTasks';
import AdminService from '../../setting/admin/AdminService';
import { getShopDisplayName } from '../../../utils/shopNameMapping';

const PRESET_SHOPS = [
  { value: 'tama', label: 'TamaChan (ทามะจัง)' },
  { value: 'smile', label: 'Smile NSG' }
];

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    height: 'auto',
    width: '500px',
    maxHeight: '90vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)'
  }
};

function shopAccentClass(shop) {
  const s = (shop || '').toLowerCase();
  if (s === 'tama') return 'border-amber-400 bg-amber-50/30';
  if (s === 'smile') return 'border-sky-400 bg-sky-50/30';
  return 'border-slate-300 bg-slate-50/40';
}

function shopPillClass(shop) {
  const s = (shop || '').toLowerCase();
  if (s === 'tama') return 'bg-amber-100 text-amber-950 ring-1 ring-amber-200/80';
  if (s === 'smile') return 'bg-sky-100 text-sky-950 ring-1 ring-sky-200/80';
  return 'bg-slate-100 text-slate-800 ring-1 ring-slate-200';
}

function deriveShopFields(shopValue) {
  const s = (shopValue || '').trim().toLowerCase();
  const preset = PRESET_SHOPS.find((p) => p.value === s);
  if (preset) {
    return { shopSelect: preset.value, shopCustom: '' };
  }
  return { shopSelect: 'custom', shopCustom: shopValue || '' };
}

const emptyForm = () => ({
  id: '',
  adminId: '',
  task: '',
  assignDate: new Date().toISOString().slice(0, 10),
  notes: '',
  shopSelect: 'tama',
  shopCustom: ''
});

const AdminSpecialTaskRecord = () => {
  const [mode, setMode] = useState('register');
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [record, setRecord] = useState(emptyForm);
  const [admins, setAdmins] = useState([]);
  const [filterAdminId, setFilterAdminId] = useState('');
  const [filterShop, setFilterShop] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  const { tasks, isLoading, error, addTask, editTask, deleteTask, refreshTasks } =
    useManageAdminSpecialTasks();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await AdminService.getAdmins();
        setAdmins(res.data);
      } catch (err) {
        console.error(err);
      }
    };
    load();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterAdminId, filterShop, filterDateFrom, filterDateTo]);

  const filteredTasks = useMemo(() => {
    return tasks.filter((t) => {
      if (filterAdminId && t.adminId !== parseInt(filterAdminId, 10)) return false;
      if (filterShop && (t.shop || '').toLowerCase() !== filterShop.toLowerCase()) return false;
      if (filterDateFrom && t.assignDate < filterDateFrom) return false;
      if (filterDateTo && t.assignDate > filterDateTo) return false;
      return true;
    });
  }, [tasks, filterAdminId, filterShop, filterDateFrom, filterDateTo]);

  const shopCounts = useMemo(() => {
    const m = {};
    filteredTasks.forEach((t) => {
      const key = (t.shop || '—').toLowerCase();
      m[key] = (m[key] || 0) + 1;
    });
    return m;
  }, [filteredTasks]);

  const uniqueShopsForFilter = useMemo(() => {
    const set = new Set(tasks.map((t) => (t.shop || '').trim()).filter(Boolean));
    return Array.from(set).sort();
  }, [tasks]);

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const pageRows = filteredTasks.slice(indexOfFirstItem, indexOfLastItem);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRecord((prev) => ({ ...prev, [name]: value }));
  };

  const resolveShopForApi = () => {
    if (record.shopSelect === 'custom') {
      return (record.shopCustom || '').trim();
    }
    return record.shopSelect;
  };

  const handleEdit = (id) => {
    const row = tasks.find((t) => t.id === id);
    if (!row) return;
    const { shopSelect, shopCustom } = deriveShopFields(row.shop);
    setRecord({
      id: row.id,
      adminId: row.adminId,
      task: row.task || '',
      assignDate: row.assignDate || '',
      notes: row.notes || '',
      shopSelect,
      shopCustom
    });
    setMode('edit');
  };

  const handleResetForm = () => {
    setRecord(emptyForm());
    setMode('register');
  };

  const openConfirm = (id) => {
    setDeleteTargetId(id);
    setIsConfirmOpen(true);
  };

  const closeConfirm = () => {
    setDeleteTargetId(null);
    setIsConfirmOpen(false);
  };

  const payloadFromForm = () => {
    const shop = resolveShopForApi();
    return {
      adminId: parseInt(record.adminId, 10),
      task: record.task,
      assignDate: record.assignDate,
      notes: record.notes || null,
      shop
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const shop = resolveShopForApi();
    if (!shop) {
      toast.error('Please select or enter a shop');
      return;
    }
    try {
      const body = payloadFromForm();
      if (!body.adminId) {
        toast.error('Please select an admin');
        return;
      }
      if (mode === 'edit') {
        await editTask(record.id, body);
        toast.success('Task updated successfully!');
        handleResetForm();
      } else {
        const ok = await addTask(body);
        if (ok) {
          toast.success('Task assigned successfully!');
          handleResetForm();
        } else {
          toast.error(typeof error === 'string' ? error : 'Failed to create');
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  const handleDelete = async () => {
    if (deleteTargetId == null) return;
    try {
      await deleteTask(deleteTargetId);
      toast.success('Task removed successfully!');
      closeConfirm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'An unexpected error occurred.');
    }
  };

  useEffect(() => {
    if (error && typeof error === 'string') {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    refreshTasks();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8 h-full pb-10">
      <div className="mt-4">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h2 className="text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900">
              Special tasks
            </h2>
            <p className="mt-1 text-slate-600 text-sm sm:text-base">
              Assign shop-specific work to admins. Table is grouped visually by shop.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Admin
                  </label>
                  <select
                    value={filterAdminId}
                    onChange={(e) => setFilterAdminId(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All</option>
                    {admins.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nickname || `Admin #${a.id}`}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    Shop
                  </label>
                  <select
                    value={filterShop}
                    onChange={(e) => setFilterShop(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="">All shops</option>
                    {uniqueShopsForFilter.map((s) => (
                      <option key={s} value={s}>
                        {getShopDisplayName(s)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    From
                  </label>
                  <input
                    type="date"
                    value={filterDateFrom}
                    onChange={(e) => setFilterDateFrom(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    value={filterDateTo}
                    onChange={(e) => setFilterDateTo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>
            </div>

            {Object.keys(shopCounts).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {Object.entries(shopCounts).map(([key, n]) => (
                  <span
                    key={key}
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${shopPillClass(key)}`}
                  >
                    <span>{getShopDisplayName(key)}</span>
                    <span className="tabular-nums opacity-80">{n}</span>
                  </span>
                ))}
              </div>
            )}

            <div className="rounded-2xl border border-slate-200/90 bg-white shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full text-left">
                  <thead>
                    <tr className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-200">
                      <th className="py-3 pl-4 pr-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-10">
                        #
                      </th>
                      <th className="py-3 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Admin
                      </th>
                      <th className="py-3 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Shop
                      </th>
                      <th className="py-3 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 min-w-[200px]">
                        Task
                      </th>
                      <th className="py-3 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 max-w-[160px]">
                        Notes
                      </th>
                      <th className="py-3 px-2 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                        Assigned
                      </th>
                      <th className="py-3 pr-4 pl-2 text-xs font-semibold uppercase tracking-wider text-slate-500 w-24">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {isLoading ? (
                      <tr>
                        <td colSpan="7" className="py-12 text-center text-slate-500 text-sm">
                          Loading…
                        </td>
                      </tr>
                    ) : pageRows.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="py-12 text-center text-slate-500 text-sm">
                          No tasks match these filters.
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row, index) => {
                        const name = row.admin?.nickname || `Admin #${row.adminId}`;
                        const initial = (name && name[0]) ? name[0].toUpperCase() : '?';
                        return (
                          <tr
                            key={row.id}
                            className={`border-l-4 transition-colors hover:bg-slate-50/90 ${shopAccentClass(row.shop)}`}
                          >
                            <td className="py-3 pl-4 pr-2 text-sm tabular-nums text-slate-400">
                              {indexOfFirstItem + index + 1}
                            </td>
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-800 text-xs font-bold">
                                  {initial}
                                </span>
                                <span className="text-sm font-medium text-slate-800 truncate max-w-[140px]" title={name}>
                                  {name}
                                </span>
                              </div>
                            </td>
                            <td className="py-3 px-2">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${shopPillClass(row.shop)}`}
                              >
                                {getShopDisplayName(row.shop)}
                              </span>
                            </td>
                            <td className="py-3 px-2 text-sm text-slate-700">
                              <p className="line-clamp-2 leading-snug" title={row.task}>
                                {row.task}
                              </p>
                            </td>
                            <td className="py-3 px-2 text-xs text-slate-500">
                              <p className="line-clamp-2" title={row.notes || ''}>
                                {row.notes || '—'}
                              </p>
                            </td>
                            <td className="py-3 px-2 text-sm tabular-nums text-slate-600 whitespace-nowrap">
                              {row.assignDate}
                            </td>
                            <td className="py-3 pr-4 pl-2">
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => handleEdit(row.id)}
                                  className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50"
                                  aria-label="Edit"
                                >
                                  <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => openConfirm(row.id)}
                                  className="p-1.5 rounded-lg text-red-600 hover:bg-red-50"
                                  aria-label="Delete"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                  <p className="text-sm text-slate-600">
                    Showing{' '}
                    <span className="font-medium tabular-nums">{indexOfFirstItem + 1}</span>–
                    <span className="font-medium tabular-nums">
                      {Math.min(indexOfLastItem, filteredTasks.length)}
                    </span>{' '}
                    of <span className="font-medium tabular-nums">{filteredTasks.length}</span>
                  </p>
                  <div className="flex gap-1">
                    <button
                      type="button"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white disabled:opacity-40"
                    >
                      Prev
                    </button>
                    <button
                      type="button"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      className="px-3 py-1.5 text-sm rounded-lg border border-slate-200 bg-white disabled:opacity-40"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="xl:col-span-1">
            <div
              className={`rounded-xl shadow-md border-2 p-6 sticky top-4 ${
                mode === 'edit' ? 'border-amber-400 bg-amber-50/20' : 'border-emerald-400 bg-emerald-50/15'
              }`}
            >
              <div className="mb-5">
                <h3
                  className={`text-xl font-bold ${
                    mode === 'edit' ? 'text-amber-900' : 'text-emerald-900'
                  }`}
                >
                  {mode === 'edit' ? 'Edit assignment' : 'Assign special task'}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {mode === 'edit' ? 'Update task details' : 'Pick admin, shop, and describe the work'}
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Admin *</label>
                  <select
                    name="adminId"
                    value={record.adminId}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">Select admin</option>
                    {admins.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.nickname || `Admin #${a.id}`}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Shop *</label>
                  <select
                    name="shopSelect"
                    value={record.shopSelect}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  >
                    {PRESET_SHOPS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                    <option value="custom">อื่นๆ (ระบุเอง)</option>
                  </select>
                  {record.shopSelect === 'custom' && (
                    <input
                      type="text"
                      name="shopCustom"
                      value={record.shopCustom}
                      onChange={handleInputChange}
                      placeholder="Shop name / code"
                      className="mt-2 w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Task *</label>
                  <textarea
                    name="task"
                    value={record.task}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    placeholder="What should this admin do?"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Assign date *</label>
                  <input
                    type="date"
                    name="assignDate"
                    value={record.assignDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    value={record.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>

                <div className="flex gap-2 pt-1">
                  <button
                    type="submit"
                    className="flex-1 bg-indigo-600 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    {mode === 'edit' ? 'Update' : 'Create'}
                  </button>
                  {mode === 'edit' && (
                    <button
                      type="button"
                      onClick={handleResetForm}
                      className="flex-1 bg-slate-200 text-slate-800 px-4 py-2.5 rounded-lg font-medium hover:bg-slate-300"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <Modal isOpen={isConfirmOpen} onRequestClose={closeConfirm} style={customStyles} ariaHideApp={false}>
        <DeleteConfirm
          items={[]}
          data={record}
          closeConfirm={closeConfirm}
          confirmFunction={handleDelete}
          titleText="Remove this special task assignment?"
        />
      </Modal>
    </div>
  );
};

export default AdminSpecialTaskRecord;
